import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiImage, FiCheckCircle, FiX, FiTrash2 } = FiIcons;

const ScreenshotUploader = ({ screenshots, setScreenshots }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      const uploadedScreenshots = [];
      
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error(`File "${file.name}" is not an image.`);
        }
        
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`File "${file.name}" exceeds the 5MB size limit.`);
        }
        
        // Read file as data URL
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error(`Failed to read file "${file.name}"`));
          reader.readAsDataURL(file);
        });
        
        // Generate unique ID for the screenshot
        const id = Math.random().toString(36).substring(2, 15) + '_' + Date.now();
        
        // Create screenshot object
        uploadedScreenshots.push({
          id,
          url: dataUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
      
      // Update screenshots state with new uploads
      setScreenshots([...screenshots, ...uploadedScreenshots]);
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeScreenshot = (screenshotId) => {
    setScreenshots(screenshots.filter(screenshot => screenshot.id !== screenshotId));
  };

  const clearAllScreenshots = () => {
    setScreenshots([]);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiImage} className="text-slate-600 text-xl" />
          <h2 className="text-xl font-semibold text-slate-800">Reference Screenshots</h2>
        </div>
        {screenshots.length > 0 && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearAllScreenshots}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
            Clear All
          </motion.button>
        )}
      </div>
      
      <p className="text-sm text-slate-600 mb-4">
        Upload screenshots of your design as reference for the AI to create more accurate Elementor JSON.
      </p>
      
      <div className="mb-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className={`w-full border-2 border-dashed ${
            isUploading
              ? 'border-slate-300 bg-slate-50'
              : 'border-blue-300 bg-blue-50 hover:bg-blue-100'
          } rounded-xl p-8 flex flex-col items-center justify-center transition-colors`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
              <span className="text-slate-600">Processing...</span>
            </div>
          ) : (
            <>
              <SafeIcon icon={FiUpload} className="text-blue-500 text-3xl mb-2" />
              <span className="text-blue-700 font-medium">Click to upload screenshots</span>
              <span className="text-slate-500 text-sm mt-1">PNG, JPG, WEBP (max 5MB each)</span>
            </>
          )}
        </motion.button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>
      
      {uploadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4">
          {uploadError}
        </div>
      )}
      
      {screenshots.length > 0 && (
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">
            Uploaded Screenshots ({screenshots.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {screenshots.map((screenshot) => (
              <div key={screenshot.id} className="relative group">
                <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                  <img
                    src={screenshot.url}
                    alt={screenshot.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <button
                  onClick={() => removeScreenshot(screenshot.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={FiX} className="text-xs" />
                </button>
                <div
                  className="text-xs text-slate-500 mt-1 truncate"
                  title={screenshot.name}
                >
                  {screenshot.name} ({(screenshot.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {screenshots.length > 0 && (
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiCheckCircle} className="text-green-600 mt-0.5" />
            <div className="text-sm text-green-700">
              Your screenshots will be used as reference to generate more accurate Elementor JSON.
              <br />
              The AI will analyze the visual layout and styling to create a better match.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenshotUploader;